import { DEFAULT_SHARE_EXPIRY, type ShareExpiry } from '@see-stella/types'
import { useEffect, useState } from 'react'
import type { Includes } from './useOwnerShare'
import './ShareSheet.css'

interface ShareSheetProps {
  includes: Includes
  creating: boolean
  error: string | null
  onCreate: (expiry: ShareExpiry) => void
  onClose: () => void
}

const SECTION_LABELS: { key: keyof Includes; label: string }[] = [
  { key: 'photos', label: 'Photos' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'routine', label: 'Routine' },
]

// The plan's dropdown (60d exists in the enum but is intentionally not offered here).
const EXPIRY_OPTIONS: { value: ShareExpiry; label: string }[] = [
  { value: '24h', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'never', label: 'Never' },
]

/**
 * The Create step: a bottom sheet that recaps exactly what will be shared (so the
 * owner sees it one more time before the link exists), lets them pick an expiry,
 * and mints + opens the OS share sheet. Manage state (live link) lands in a later
 * slice. Owner-facing copy ("You're sharing") — the recipient-facing copy lives in
 * the shared profile itself.
 */
export function ShareSheet({
  includes,
  creating,
  error,
  onCreate,
  onClose,
}: ShareSheetProps) {
  const [expiry, setExpiry] = useState<ShareExpiry>(DEFAULT_SHARE_EXPIRY)

  // Escape closes the sheet (standard dialog affordance alongside the scrim).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const shared = SECTION_LABELS.filter((s) => includes[s.key]).map(
    (s) => s.label
  )
  const notShared = SECTION_LABELS.filter((s) => !includes[s.key]).map(
    (s) => s.label
  )

  return (
    <div
      className='sheet'
      role='dialog'
      aria-modal='true'
      aria-label='Share your skin profile'
    >
      <button
        type='button'
        className='sheet__scrim'
        aria-label='Close'
        onClick={onClose}
      />
      <div className='sheet__card'>
        <div className='sheet__grip' aria-hidden='true' />
        <h2 className='sheet__title'>Share your skin profile</h2>

        <div className='sheet__recap'>
          <p className='sheet__recap-line'>
            <b>You're sharing:</b> {shared.join(' · ')}
          </p>
          {notShared.length > 0 && (
            <p className='sheet__recap-note'>
              {notShared.join(' · ')} not included.
            </p>
          )}
        </div>

        <label className='sheet__field'>
          <span className='sheet__label'>Link expires</span>
          <select
            className='sheet__select'
            value={expiry}
            disabled={creating}
            onChange={(e) => setExpiry(e.target.value as ShareExpiry)}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        {error && (
          <p className='sheet__error' role='alert'>
            {error}
          </p>
        )}

        <button
          type='button'
          className='sheet__cta'
          disabled={creating}
          onClick={() => onCreate(expiry)}
        >
          {creating ? 'Creating…' : 'Create & share'}
        </button>

        <p className='sheet__foot'>
          Anyone with the link can view it. You can change what is shared or
          stop sharing at any time.
        </p>
      </div>
    </div>
  )
}
